// src/pages/DashboardTechnician.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, 
  Avatar, LinearProgress, alpha, useTheme, Chip, Stack,
  List, ListItem, ListItemText, ListItemIcon, Divider
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Build,
  Warning,
  CheckCircle,
  Schedule,
  LocalLaundryService,
  Assignment,
  Report,
  ListAlt,
  BugReport,
  BarChart,
  Inventory
} from '@mui/icons-material';
import { getData } from '../services/storageService';
import InfoWidget from '../components/InfoWidget';
import SmartRemindersPanel from '../components/SmartRemindersPanel';
import { PartsInventoryService } from '../services/partsInventoryService';
import { WorkOrderService } from '../services/workOrderService';
import { useI18n } from '../i18n/i18n';

// 🎨 Компонент картки статусу обладнання
const EquipmentStatusCard = ({ title, status, value, maxValue, icon: Icon, color = "primary", t }) => {
  const theme = useTheme();
  const progress = maxValue ? (value / maxValue) * 100 : 0;
  
  const statusConfig = {
    operational: { color: 'success', label: t('dashboard.tech.operational', 'Operacional') },
    maintenance: { color: 'warning', label: t('dashboard.tech.maintenance', 'En Mantenimiento') },
    critical: { color: 'error', label: t('anomaly_critical', 'Crítico') },
    offline: { color: 'default', label: t('dashboard.tech.offline', 'Fuera de Línea') }
  };

  const config = statusConfig[status] || statusConfig.operational;

  return (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${alpha(theme.palette[config.color].main, 0.1)} 0%, ${alpha(theme.palette[config.color].light, 0.05)} 100%)`,
      border: `2px solid ${alpha(theme.palette[config.color].main, 0.2)}`,
      borderRadius: 3,
      position: 'relative',
      overflow: 'visible'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[config.color].dark }}>
              {title}
            </Typography>
            <Chip 
              label={config.label} 
              color={config.color}
              size="small"
              sx={{ fontWeight: 600, mt: 0.5 }}
            />
          </Box>
          <Avatar sx={{ 
            bgcolor: alpha(theme.palette[config.color].main, 0.1), 
            width: 48, 
            height: 48,
            border: `2px solid ${alpha(theme.palette[config.color].main, 0.2)}`
          }}>
            <Icon sx={{ color: theme.palette[config.color].main, fontSize: 24 }} />
          </Avatar>
        </Box>
        
        {maxValue && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.tech.utilization', 'Utilización')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {value}/{maxValue}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress}
              color={config.color}
              sx={{ 
                height: 8,
                borderRadius: 4,
                mb: 2
              }}
            />
          </>
        )}

        <Typography variant="h4" sx={{ 
          fontWeight: 800, 
          color: theme.palette[config.color].dark,
          textAlign: 'center'
        }}>
          {value}
          {maxValue && (
            <Typography component="span" variant="h6" sx={{ 
              fontWeight: 600, 
              color: theme.palette.text.secondary,
              ml: 0.5
            }}>
              /{maxValue}
            </Typography>
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

// 🎨 Компонент картки швидкого доступу для техніка
const TechQuickAccessCard = ({ title, description, icon: Icon, color = "primary", to, alertCount = 0, t }) => {
  const theme = useTheme();
  return (
    <Card sx={{ 
      height: '100%',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
      },
      border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
      borderRadius: 3,
      position: 'relative'
    }}>
      {alertCount > 0 && (
        <Chip 
          label={alertCount} 
          color="error" 
          size="small"
          sx={{ 
            position: 'absolute', 
            top: -8, 
            right: -8,
            fontWeight: 700
          }}
        />
      )}
      <CardContent sx={{ p: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar sx={{ 
            bgcolor: alpha(theme.palette[color].main, 0.1), 
            width: 48, 
            height: 48,
            mr: 2,
            border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`
          }}>
            <Icon sx={{ color: theme.palette[color].main, fontSize: 24 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {description}
            </Typography>
          </Box>
        </Box>
        
        <Button
          component={RouterLink}
          to={to}
          variant="contained"
          color={color}
          fullWidth
          sx={{ 
            height: 36,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.875rem'
          }}
        >
          {t('dashboard.actions.access')}
        </Button>
      </CardContent>
    </Card>
  );
};

// 🎨 Компонент списку завдань техобслуговування
const MaintenanceTasksList = ({ tasks, t }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };
  
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return t('ai_priority_high');
      case 'medium': return t('ai_priority_medium');
      case 'low': return t('ai_priority_low');
      default: return priority;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle sx={{ color: theme.palette.success.main }} />;
      case 'in-progress': return <Schedule sx={{ color: theme.palette.warning.main }} />;
      default: return <Warning sx={{ color: theme.palette.error.main }} />;
    }
  };

  if (!tasks || tasks.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {t('no_tasks')}
          </Typography>
          <Typography color="text.secondary">
            {t('dashboard.tech.all_tasks_done')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('nav_maintenance')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.tech.tasks_pending', { count: tasks.length })}
          </Typography>
        </Box>
        
        <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
          {tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              <ListItem 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
                onClick={() => navigate('/maintenance')}
              >
                <ListItemIcon>
                  {getStatusIcon(task.status)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {task.machine}
                      </Typography>
                      <Chip 
                        label={getPriorityLabel(task.priority)}
                        color={getPriorityColor(task.priority)}
                        size="small"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {task.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('dashboard.tech.due_date', { date: task.dueDate })}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {index < tasks.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        
        <Box sx={{ p: 2, pt: 1 }}>
          <Button 
            fullWidth 
            variant="outlined"
            onClick={() => navigate('/maintenance')}
            sx={{ borderRadius: 2 }}
          >
            {t('dashboard.tech.view_all_tasks')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function DashboardTechnician() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [workOrderService] = useState(() => new WorkOrderService());
  const [inventoryService] = useState(() => new PartsInventoryService());
  const [equipmentStats, setEquipmentStats] = useState({
    operational: 18,
    maintenance: 3,
    critical: 1,
    offline: 2
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try { 
        const u = await getData('current_user'); 
        if (alive) setUser(u); 
      } catch {}
      
      try {
        const m = await getData('maintenance');
        const arr = Array.isArray(m) ? m : [];
        if (!alive) return;
        
        const norm = arr.map((x) => ({
          id: x.id || `${x.machineId || x.machine || 'M'}-${x.dueDate || x.date || Math.random()}`,
          machine: x.machineName || x.name || (x.machineId ? `${t('machine')} ${x.machineId}` : t('machine')),
          description: x.description || t('maintlog_event_type_planned'),
          dueDate: x.dueDate ? new Date(x.dueDate).toLocaleDateString() : t('common.not_specified'),
          priority: x.priority || 'medium',
          status: x.status || 'pending'
        }))
        .filter(x => x.dueDate)
        .sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 6);
        
        setTasks(norm);
      } catch {
        setTasks([]);
      }

      setEquipmentStats({
        operational: 18,
        maintenance: 3,
        critical: 1,
        offline: 2
      });
    })();
  }, [t]);

  const totalMachines = useMemo(() => {
    return equipmentStats.operational + equipmentStats.maintenance + equipmentStats.critical + equipmentStats.offline;
  }, [equipmentStats]);

  const operationalPercentage = useMemo(() => {
    return totalMachines > 0 ? Math.round((equipmentStats.operational / totalMachines) * 100) : 0;
  }, [equipmentStats, totalMachines]);

  const lowStockCount = inventoryService.getPartsNeedingRestock().length;
  const assignedWorkOrders = workOrderService.getWorkOrdersForTechnician(user?.username || 'tech_001')
    .filter(wo => wo.status === 'assigned').length;

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.info.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          {t('panel_technician')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {user?.username ? t('hello_name', { name: user.username }) : t('dashboard.tech.subtitle')}
        </Typography>
      </Box>

      {/* InfoWidget */}
      <Box sx={{ mb: 4 }}>
        <InfoWidget />
      </Box>

      {/* Smart Reminders Panel */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <SmartRemindersPanel user={user} />
        </Grid>
      </Grid>

      {/* Статус обладнання */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        {t('dashboard.tech.equipment_status')}
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <EquipmentStatusCard
            title={t('dashboard.tech.operational')}
            status="operational"
            value={equipmentStats.operational}
            maxValue={totalMachines}
            icon={CheckCircle}
            color="success"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EquipmentStatusCard
            title={t('dashboard.tech.maintenance')}
            status="maintenance"
            value={equipmentStats.maintenance}
            icon={Build}
            color="warning"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EquipmentStatusCard
            title={t('anomaly_critical')}
            status="critical"
            value={equipmentStats.critical}
            icon={Warning}
            color="error"
            t={t}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <EquipmentStatusCard
            title={t('dashboard.tech.availability')}
            status="operational"
            value={`${operationalPercentage}%`}
            icon={CheckCircle}
            color="info"
            t={t}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Колонка 1: Завдання та швидкий доступ */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            {t('dashboard.tech.priority_tasks')}
          </Typography>
          <MaintenanceTasksList tasks={tasks} t={t} />
        </Grid>

        {/* Колонка 2: Швидкий доступ */}
        <Grid item xs={12} lg={4}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            {t('dashboard.quick_actions')}
          </Typography>
          
          {/* Перший ряд - 2 картки */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('nav_tech_report')}
                description={t('technician_daily_report_desc')}
                icon={Report}
                color="primary"
                to="/technician-report"
                t={t}
              />
            </Grid>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('tech_form_title')}
                description={t('technician_form_desc')}
                icon={ListAlt}
                color="secondary"
                to="/technician-form"
                t={t}
              />
            </Grid>
          </Grid>

          {/* Другий ряд - 2 картки */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('nav_equipment')}
                description={t('dashboard.tech.machines_desc')}
                icon={LocalLaundryService}
                color="info"
                to="/equipment"
                alertCount={equipmentStats.critical}
                t={t}
              />
            </Grid>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('nav_maintenance')}
                description={t('dashboard.tech.maintenance_desc')}
                icon={Schedule}
                color="warning"
                to="/maintenance"
                alertCount={tasks.filter(t => t.priority === 'high').length}
                t={t}
              />
            </Grid>
          </Grid>

          {/* Третій ряд - 2 картки */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('technician_analysis_title')}
                description={t('technician_analysis_subtitle')}
                icon={BarChart}
                color="success"
                to="/technician-stats"
                t={t}
              />
            </Grid>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('recurring_issues_title')}
                description={t('recurring_issues_subtitle')}
                icon={BugReport}
                color="error"
                to="/recurring-issues"
                alertCount={5}
                t={t}
              />
            </Grid>
          </Grid>

          {/* Четвертий ряд - 2 картки */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('archive.tabs.technician')}
                description={t('archive.subtitle')}
                icon={Assignment}
                color="secondary"
                to="/archive/technician"
                t={t}
              />
            </Grid>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('ironing_assignments_title')}
                description={t('ironing_assignments_subtitle')}
                icon={LocalLaundryService}
                color="primary"
                to="/iron-assignments"
                t={t}
              />
            </Grid>
          </Grid>

          {/* П'ятий ряд - 2 картки */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title={t('parts_inventory_title')}
                description={t('parts_inventory_subtitle')}
                icon={Inventory}
                color="info"
                to="/parts-inventory"
                alertCount={lowStockCount}
                t={t}
              />
            </Grid>
            <Grid item xs={6}>
              <TechQuickAccessCard
                title="Mobile Work Orders"
                description="Field service and task management"
                icon={Assignment}
                color="primary"
                to="/mobile-work-orders"
                alertCount={assignedWorkOrders}
                t={t}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Статистика продуктивності */}
      <Card sx={{ mt: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            {t('dashboard.tech.maintenance_efficiency')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.main }}>
                  94%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.tech.tasks_completed')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                  2.3h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.tech.avg_time')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.warning.main }}>
                  12
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.tech.mttr')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                  98.5%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.tech.availability')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}